import { FC } from 'react';
import type { Theme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Badge, type BadgeVariant } from '@components/Badge';
import { RemediationItemStatus, RemediationRunStatus } from '@libs/constants';
import { type SingularKeys, useTrans } from '@libs/i18n';

/**
 * Maps remediation statuses onto the shared Badge's existing variants, so run
 * and item state reads the same as assessment state everywhere else in the app.
 *
 * The mapping is deliberate rather than mechanical:
 * - READY_FOR_REVIEW borrows `Submitted` (amber) because it is a "waiting on
 *   you" state, not a success.
 * - DISCARDED and REJECTED are neutral, not errors — a reviewer choosing not to
 *   apply something is a normal outcome, and colouring it red would read as a
 *   malfunction.
 * - NO_CHANGES is neutral for the same reason: the AI finding nothing to fix is
 *   a good result, but it is not an accepted change.
 */
const RUN_VARIANT: Record<RemediationRunStatus, BadgeVariant> = {
  [RemediationRunStatus.PENDING]: 'Draft',
  [RemediationRunStatus.PROCESSING]: 'Processing',
  [RemediationRunStatus.READY_FOR_REVIEW]: 'Submitted',
  [RemediationRunStatus.FINALIZING]: 'Processing',
  [RemediationRunStatus.COMPLETED]: 'Completed',
  [RemediationRunStatus.DISCARDED]: 'neutral',
  [RemediationRunStatus.FAILED]: 'Failed',
};

const ITEM_VARIANT: Record<RemediationItemStatus, BadgeVariant> = {
  [RemediationItemStatus.PENDING]: 'Draft',
  [RemediationItemStatus.PROCESSING]: 'Processing',
  [RemediationItemStatus.REVIEW_PENDING]: 'Submitted',
  [RemediationItemStatus.ACCEPTED]: 'Completed',
  [RemediationItemStatus.REJECTED]: 'neutral',
  [RemediationItemStatus.NO_CHANGES]: 'neutral',
  [RemediationItemStatus.FAILED]: 'Failed',
};

/**
 * Where a completed assessment stands with respect to remediation, as shown in
 * the screen 2 table.
 *
 * Derived from the tenant's runs by `RemediationListPage`, which asks for the
 * terminal statuses as well as the active ones. `needsReview` and `failed` are
 * split out from the run status rather than folded into `inProgress` because
 * they are the two states where the row is actionable by a person: one is
 * waiting on a decision, the other on a retry, and both used to read as "still
 * working" — or, for a failed run, as "never remediated".
 */
export type AssessmentRemediationState =
  | 'none'
  | 'inProgress'
  | 'needsReview'
  | 'assessing'
  | 'reassessing'
  | 'partial'
  | 'full'
  | 'failed';

const ASSESSMENT_VARIANT: Record<AssessmentRemediationState, BadgeVariant> = {
  none: 'neutral',
  // Solid blue, as in the design — the one state that is actively changing.
  inProgress: 'Processing',
  // Amber, the same "waiting on you" colour READY_FOR_REVIEW gets everywhere else.
  needsReview: 'Submitted',
  // Both blue: from the user's side these are the same kind of wait as
  // generating, just a different stage. Neither is actionable — an assessment
  // being graded has no frozen results for a scope to be built from.
  assessing: 'Processing',
  reassessing: 'Processing',
  partial: 'warningSoft',
  full: 'successSoft',
  failed: 'Failed',
};

/** Dot beside the assessment title; same encoding as the badge. */
export const ASSESSMENT_STATE_DOT: Record<
  AssessmentRemediationState,
  (theme: Theme) => string
> = {
  none: (theme) => theme.palette.text.tertiary,
  inProgress: (theme) => theme.palette.blue[700],
  needsReview: (theme) => theme.palette.warning.main,
  assessing: (theme) => theme.palette.blue[700],
  reassessing: (theme) => theme.palette.blue[700],
  partial: (theme) => theme.palette.yellow[700],
  full: (theme) => theme.palette.green[700],
  failed: (theme) => theme.palette.error.main,
};

/**
 * States where the row is waiting on the user rather than on the AI.
 *
 * Drives the left edge marker and the "needs my attention" filter — the eye
 * needs somewhere to land on a page of otherwise identical rows.
 */
export const ATTENTION_STATES: readonly AssessmentRemediationState[] = ['needsReview', 'failed'];

export const AssessmentRemediationBadge: FC<{
  state: AssessmentRemediationState;
  /**
   * Documents awaiting a decision. Only read for `needsReview`, where the label
   * carries the number — "3 documents need review" tells the user how much work
   * the row is asking for, which is what decides whether they open it now.
   */
  reviewCount?: number;
}> = observer(({ state, reviewCount }) => {
  const { t } = useTrans();

  const label =
    state === 'needsReview' && reviewCount
      ? t('app.remediation.list.state.needsReviewCount', { count: reviewCount })
      : t(`app.remediation.list.state.${state}` as SingularKeys);

  return <Badge variant={ASSESSMENT_VARIANT[state]} label={label} />;
});

export const RunStatusBadge: FC<{ status: RemediationRunStatus }> = observer(({ status }) => {
  const { t } = useTrans();
  return (
    <Badge
      variant={RUN_VARIANT[status]}
      label={t(`app.remediation.status.${status}` as SingularKeys)}
    />
  );
});

export const ItemStatusBadge: FC<{
  status: RemediationItemStatus;
  /**
   * The owning run's status. Accepted for call-site compatibility and no longer used
   * to derive the badge.
   *
   * It used to be: the backend never assigned PROCESSING — every item sat at PENDING
   * until the whole batch returned — so an actively-running job showed every document
   * as "Queued", and this substituted PROCESSING whenever the run was working. The
   * backend now moves items to PROCESSING at dispatch and back out as each document
   * arrives, so the raw status is accurate and substituting would actively lie:
   * a genuinely queued document would be labelled as being written.
   */
  runStatus?: RemediationRunStatus;
}> = observer(({ status }) => {
  const { t } = useTrans();

  return (
    <Badge
      variant={ITEM_VARIANT[status]}
      label={t(`app.remediation.itemStatus.${status}` as SingularKeys)}
    />
  );
});
